import { Injectable, inject } from '@angular/core';
import { AnalyticsEventName, AnalyticsService } from '@lite-workspace/shared/services/analytics';

export type WizardPhase = 'design' | 'project';
export type WizardMode = 'select' | 'upload';
/** Кто прислал строку. `design_wizard` — мастер шаблонов: у него та же форма строки, что и у мастера
 *  проекта в UPLOAD (`phase: 'design'`, `mode: 'upload'`), и без поверхности их не различить. */
export type WizardSurface = 'onboarding' | 'project_wizard' | 'design_wizard';
export type TemplateSelectTrigger =
  | 'default_kept'
  | 'catalog_picked'
  | 'upload_own_clicked'
  | 'catalog_switch_clicked'
  | 'free_design_escape_clicked'
  | 'review_pack_reselected';

/** Sentinel pack ids for the two selections that are not a catalog design. */
export const FREE_PACK_ID = 'free';
export const OWN_UPLOAD_PACK_ID = 'own_upload';

const DESIGN_STEP_EVENTS: readonly AnalyticsEventName[] = [
  AnalyticsEventName.WIZARD_S1_TEMPLATES_UPLOADED,
  AnalyticsEventName.WIZARD_S2_FONTS_COMPLETED,
];

@Injectable({ providedIn: 'root' })
export class WizardAnalyticsService {
  private readonly analytics = inject(AnalyticsService);

  /** Модалка «Создать проект» открылась — верх воронки до `wizard_started`. */
  createProjectClicked(): void {
    this.analytics.track(AnalyticsEventName.CREATE_PROJECT_CLICKED);
  }

  started(projectId: string): void {
    this.analytics.track(AnalyticsEventName.WIZARD_STARTED, { project_id: projectId });
  }

  completed(input: { projectId: string; durationSec?: number; designId?: string }): void {
    this.analytics.track(AnalyticsEventName.WIZARD_COMPLETED, {
      project_id: input.projectId,
      total_duration_sec: input.durationSec,
      ...(input.designId ? { design_id: input.designId } : {}),
    });
  }

  templateSelected(input: {
    mode: WizardMode;
    packId: string;
    isDefaultPack: boolean;
    surface: WizardSurface;
    trigger: TemplateSelectTrigger;
    projectId?: string;
  }): void {
    this.analytics.track(AnalyticsEventName.WIZARD_TEMPLATE_SELECTED, {
      mode: input.mode,
      pack_id: input.packId,
      is_default_pack: input.isDefaultPack,
      surface: input.surface,
      trigger: input.trigger,
      ...(input.projectId ? { project_id: input.projectId } : {}),
    });
  }

  /** Design-phase step completion (s1-s2); forward-only guarding is the caller's. */
  designStepCompleted(input: { step: 1 | 2; designId?: string }): void {
    this.analytics.track(DESIGN_STEP_EVENTS[input.step - 1], {
      ...(input.designId ? { design_id: input.designId } : {}),
    });
  }

  /**
   * `surface` — единственное, что разводит шкалы `last_step`: у мастера проекта SELECT это 1-4, у
   * онбординга 1-5, режим у обоих `select`. Без него одно и то же число читается как два разных шага,
   * поэтому поле обязательное: необязательное молча пропускал третий производитель.
   */
  abandoned(input: {
    lastStep: number;
    phase: WizardPhase;
    mode: WizardMode;
    surface: WizardSurface;
    projectId?: string;
    designId?: string;
  }): void {
    this.analytics.track(AnalyticsEventName.WIZARD_ABANDONED, {
      last_step: input.lastStep,
      phase: input.phase,
      mode: input.mode,
      surface: input.surface,
      ...(input.projectId ? { project_id: input.projectId } : {}),
      ...(input.designId ? { design_id: input.designId } : {}),
    });
  }
}
